"use client";

import { ShoppingCart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useId, useMemo, useState } from "react";

import { useCart } from "@/components/cart/cart-provider";
import { toSessionCartItem } from "@/components/catalog/add-to-cart-button";
import { trackCatalogEvent } from "@/components/catalog/analytics";
import { QuantityStepper } from "@/components/catalog/quantity-stepper";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { subtleTween, usePressedInteraction } from "@/components/ui/motion-primitives";
import { useTemporaryFeedback } from "@/components/ui/use-temporary-feedback";
import { useMorphTargetVisibility } from "@/components/whatsapp/use-morph-target-visibility";
import { isEcommercePreviewReadOnly } from "@/config/preview-mode";
import type { PublicCatalogProduct } from "@/domain/catalog/catalog-product";

export interface ProductDetailActionsProps {
  readonly product: PublicCatalogProduct;
  readonly whatsappPhone: string;
}

export function ProductDetailActions({
  product,
  whatsappPhone,
}: Readonly<ProductDetailActionsProps>) {
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const actionInteraction = usePressedInteraction();
  const { isVisible: isAdded, showFeedback } = useTemporaryFeedback();
  const { isTargetVisible, targetRef } = useMorphTargetVisibility<HTMLDivElement>();
  const isReadOnlyPreview = isEcommercePreviewReadOnly();
  const isOutOfStock = product.availability === "out_of_stock";
  const whatsappHref = useMemo(() => {
    const message = [
      "Hola, quiero consultar este producto:",
      product.shortName ?? product.name,
      `Producto: ${product.name}`,
      `Link: /productos/${product.slug}`,
    ].join("\n");

    return `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`;
  }, [product, whatsappPhone]);

  function addProductToCart() {
    if (isOutOfStock) {
      return;
    }

    addItem(toSessionCartItem(product, quantity), quantity);
    showFeedback();
    trackCatalogEvent({
      eventName: "product_add_to_cart",
      productId: product.id,
      sku: product.sku,
      payload: {
        availability: product.availability,
        brandSlug: product.brandSlug,
        familySlug: product.familySlug,
        productType: product.type,
        quantity,
        source: "product_detail",
      },
    });
  }

  return (
    <>
      <section className="space-y-3">
        <ProductCartActionRow
          isAdded={isAdded}
          isOutOfStock={isOutOfStock}
          onAdd={addProductToCart}
          onQuantityChange={setQuantity}
          quantity={quantity}
          ref={targetRef}
        />
        {!isReadOnlyPreview ? (
          <motion.a
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-secondary px-4 text-sm font-extrabold text-white"
            href={whatsappHref}
            onClick={() =>
              trackCatalogEvent(
                {
                  eventName: "whatsapp_product_consult_click",
                  productId: product.id,
                  payload: {
                    brandSlug: product.brandSlug,
                    familySlug: product.familySlug,
                    productType: product.type,
                    source: "product_detail",
                  },
                  sku: product.sku,
                },
                { keepalive: true },
              )
            }
            rel="noreferrer"
            {...actionInteraction}
          >
            <WhatsAppIcon className="h-5 w-5 brightness-0 invert" variant="mono" />
            Consultar por WhatsApp
          </motion.a>
        ) : null}
      </section>
      <AnimatePresence>
        {!isTargetVisible ? (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-[76px] left-0 right-0 z-30 px-4"
            exit={{ opacity: 0, y: 12 }}
            initial={{ opacity: 0, y: 12 }}
            key="product-cart-floating-row"
            transition={subtleTween}
          >
            <ProductCartActionRow
              isAdded={isAdded}
              isOutOfStock={isOutOfStock}
              onAdd={addProductToCart}
              onQuantityChange={setQuantity}
              quantity={quantity}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}

interface ProductCartActionRowProps {
  readonly isAdded: boolean;
  readonly isOutOfStock: boolean;
  readonly onAdd: () => void;
  readonly onQuantityChange: (quantity: number) => void;
  readonly quantity: number;
  readonly ref?: React.Ref<HTMLDivElement>;
}

function ProductCartActionRow({
  isAdded,
  isOutOfStock,
  onAdd,
  onQuantityChange,
  quantity,
  ref,
}: Readonly<ProductCartActionRowProps>) {
  const interaction = usePressedInteraction();
  const {
    isVisible: isOutOfStockMessageVisible,
    showFeedback: showOutOfStockMessage,
  } = useTemporaryFeedback(2500);
  const outOfStockMessageId = useId();

  function handleAdd() {
    if (isOutOfStock) {
      showOutOfStockMessage();
      return;
    }

    onAdd();
  }

  return (
    <div className="mx-auto w-full max-w-md" ref={ref}>
      <div className="flex items-center gap-3 rounded-lg bg-background">
        <div className="shrink-0">
          <QuantityStepper onChange={onQuantityChange} value={quantity} />
        </div>
        <motion.button
          aria-describedby={isOutOfStockMessageVisible ? outOfStockMessageId : undefined}
          aria-disabled={isOutOfStock}
          aria-live="polite"
          className={`inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-extrabold outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${isOutOfStock ? "cursor-not-allowed bg-primary-fixed text-primary" : "bg-primary text-white"}`}
          onClick={handleAdd}
          type="button"
          {...interaction}
        >
          <ShoppingCart aria-hidden="true" size={16} />
          {isAdded ? "Agregado" : "Agregar al pedido"}
        </motion.button>
      </div>
      {isOutOfStockMessageVisible ? (
        <p
          className="mt-1 text-center text-xs font-semibold text-red-600"
          id={outOfStockMessageId}
          role="alert"
        >
          Este producto no tiene stock disponible.
        </p>
      ) : null}
    </div>
  );
}
